/**
 * Represents an execution state, which tracks the current state of a work order execution.
 *
 * @class ExecutionState
 * @property {string} id - ID of the execution state document
 * @property {string} currentState - Current execution state; see `@src/models/execution`
 * @property {Date} createdAt - Creation timestamp
 * @property {Date} updatedAt - Last update timestamp
 * @property {string} uid - User ID who last updated the state
 * @property {string} organizationId - Organization ID
 * @property {string} divisionId - Division ID
 * @property {string} executionId - Execution ID
 * @property {{uid: string, reason: string, at: Date|null}|null} supervisorClosure - Set when a
 *   supervisor marked it done on behalf of its assignees (ADR 0002)
 */

class ExecutionState {
  constructor(
    id,
    currentState,
    createdAt,
    updatedAt,
    uid,
    organizationId,
    divisionId,
    executionId,
    supervisorClosure = null,
  ) {
    this.id = id;
    this.currentState = currentState;
    this.createdAt = createdAt;
    this.updatedAt = updatedAt;
    this.uid = uid;
    this.organizationId = organizationId;
    this.divisionId = divisionId;
    this.executionId = executionId;
    this.supervisorClosure = supervisorClosure;
  }

  /**
   * Create ExecutionState instance from Firestore document data
   * @param {Object|null|undefined} data - Firestore document data
   * @param {string} docId - Document ID
   * @param {string} organizationId - Organization ID
   * @param {string} divisionId - Division ID
   * @param {string} executionId - Execution ID
   * @returns {ExecutionState|null} New ExecutionState instance, or null when data is missing
   */
  static fromFirestore(data, docId, organizationId, divisionId, executionId) {
    if (!data) {
      return null;
    }

    return new ExecutionState(
      docId,
      data.currentState || null,
      data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt ?? null,
      data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt ?? null,
      data.uid || null,
      organizationId,
      divisionId,
      executionId,
      data.supervisorClosure
        ? {
            uid: data.supervisorClosure.uid,
            reason: data.supervisorClosure.reason,
            at: data.supervisorClosure.at?.toDate ? data.supervisorClosure.at.toDate() : null,
          }
        : null,
    );
  }

}

export default ExecutionState;
